import { getWeddingEvents } from '@bridebook/web/lib/events/selectors';
import { getCategories, getGuests } from '@bridebook/web/lib/guestlist/selectors';
import { useSelector } from '@bridebook/web/lib/utils';
import { useGetWeddingVenuePreferencesQuery } from '@bridebook/web/lib/weddings/hooks/use-get-wedding-venue-preferences-query';
import { getProfile } from '@bridebook/web/lib/weddings/selectors';
import { useMemo } from 'react';
import {
  summariseBudget,
  summariseChecklist,
  summariseEvents,
  summariseGuests,
  summarisePreferences,
  summariseSuppliers,
  type WeddingContextSummary,
} from '../wedding-context-summary';
import type { ResolvedChecklist } from '../planning/blueprint/types';
import { useBudgetProjection } from './use-budget-projection';
import { useResolvedChecklist } from './use-resolved-checklist';

/*
 * One hook per section of the planning summary, each subscribed to its own slice, so a
 * surface that shows a section redraws for that section alone. The request body composes
 * them all; the summary view picks the ones it draws.
 */

const toRecord = <T extends { id: string }>(
  items: readonly T[] | undefined,
): Record<string, T> | undefined =>
  items ? Object.fromEntries(items.map((item) => [item.id, item])) : undefined;

/**
 * A resolved section with whether its sources have finished loading, outcome aside, and
 * whether one of them came back empty-handed and will not settle on its own.
 */
export type SettledSection<SUMMARY> = { summary: SUMMARY; settled: boolean; failed: boolean };

/**
 * The checklist section, and the resolved checklist behind it: the browser answers the
 * task lookup from that, names and live done state included, so the summary posts none.
 */
export const useChecklistSummary = (): SettledSection<WeddingContextSummary['checklist']> & {
  resolved: ResolvedChecklist | null;
} => {
  const { checklist, blueprint, settled, failed } = useResolvedChecklist();
  const date = useSelector((state) => getProfile(state).date);
  const summary = useMemo(
    () => summariseChecklist(checklist, blueprint, date),
    [checklist, blueprint, date],
  );
  return useMemo(
    () => ({ summary, settled, failed, resolved: checklist }),
    [summary, settled, failed, checklist],
  );
};

export const useBudgetSummary = (): SettledSection<WeddingContextSummary['budget']> => {
  const { projection, settled, failed } = useBudgetProjection();
  const summary = useMemo(() => summariseBudget(projection), [projection]);
  return useMemo(() => ({ summary, settled, failed }), [summary, settled, failed]);
};

export const useGuestsSummary = (): WeddingContextSummary['guests'] => {
  const guestList = useSelector(getGuests);
  const categories = useSelector(getCategories);
  const eventList = useSelector(getWeddingEvents);
  return useMemo(
    () => summariseGuests(toRecord(guestList), toRecord(eventList), categories.data),
    [guestList, eventList, categories],
  );
};

export const useSuppliersSummary = (): WeddingContextSummary['suppliers'] => {
  const suppliers = useSelector((state) => state.shortlist.list);
  return useMemo(() => summariseSuppliers(suppliers), [suppliers]);
};

export const useEventsSummary = (): WeddingContextSummary['events'] => {
  const guestList = useSelector(getGuests);
  const eventList = useSelector(getWeddingEvents);
  return useMemo(
    () => summariseEvents(toRecord(eventList), toRecord(guestList)),
    [eventList, guestList],
  );
};

export const useVenuePreferencesSummary = (): WeddingContextSummary['preferences'] => {
  const { weddingVenueStyles, weddingVenueTypes, weddingVenueMustHaves } =
    useGetWeddingVenuePreferencesQuery();
  const hasAny =
    (weddingVenueStyles?.length ?? 0) > 0 ||
    (weddingVenueTypes?.length ?? 0) > 0 ||
    (weddingVenueMustHaves?.length ?? 0) > 0;
  return useMemo(
    () =>
      summarisePreferences(
        hasAny
          ? {
              styles: weddingVenueStyles,
              types: weddingVenueTypes,
              mustHaves: weddingVenueMustHaves,
            }
          : undefined,
      ),
    [hasAny, weddingVenueStyles, weddingVenueTypes, weddingVenueMustHaves],
  );
};
